function readCsrfTokenFromMeta(): string {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
}

export class LaravelJsonRequestError extends Error {
    readonly status: number;

    readonly body: unknown;

    constructor(message: string, status: number, body: unknown) {
        super(message);
        this.name = 'LaravelJsonRequestError';
        this.status = status;
        this.body = body;
    }
}

function extractErrorMessage(body: unknown, fallback: string): string {
    if (body && typeof body === 'object' && 'message' in body && typeof (body as { message: unknown }).message === 'string') {
        return (body as { message: string }).message;
    }

    return fallback;
}

export async function laravelJsonFetch<T = unknown>(
    url: string,
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    body?: Record<string, unknown> | null,
): Promise<T> {
    const headers: Record<string, string> = {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-TOKEN': readCsrfTokenFromMeta(),
    };

    const init: RequestInit = {
        method,
        headers,
        credentials: 'same-origin',
    };

    if (body !== undefined && body !== null) {
        headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
    }

    const response = await fetch(url, init);
    const contentType = response.headers.get('content-type') ?? '';
    const isJson = contentType.includes('application/json');
    const parsedBody: unknown = isJson ? await response.json().catch(() => null) : await response.text().catch(() => null);

    if (response.status === 419) {
        throw new LaravelJsonRequestError('Session expired. Please refresh the page.', 419, parsedBody);
    }

    if (!response.ok) {
        const message = extractErrorMessage(parsedBody, `Request failed (${response.status})`);
        throw new LaravelJsonRequestError(message, response.status, parsedBody);
    }

    return parsedBody as T;
}
